import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  InputAdornment,
  ListSubheader,
  CircularProgress,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { companyApi } from '../../services/api/companyApi';

interface LicenseeFilterProps {
  value: number | '';
  onChange: (companyId: number | '', companyName: string) => void;
  /** Hide the "All Licensees" option — use when a selection is required */
  required?: boolean;
  /** Custom label for the select (default: "Filter by Licensee") */
  label?: string;
  /** Custom styles for the FormControl wrapper */
  sx?: SxProps<Theme>;
  /** Show error state on the FormControl */
  error?: boolean;
}

const DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 10;

const LicenseeFilter: React.FC<LicenseeFilterProps> = ({
  value,
  onChange,
  required = false,
  label = 'Filter by Licensee',
  sx: sxProp,
  error = false,
}) => {
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track the currently selected company so we can always display its name,
  // even when it's not in the current search results.
  const [selectedEntry, setSelectedEntry] = useState<{ id: number; name: string } | null>(null);

  const fetchCompanies = useCallback(async (query?: string) => {
    try {
      setLoading(true);
      const res = await companyApi.getAll({
        limit: DEFAULT_LIMIT,
        fields: 'id,name',
        ...(query ? { query } : {}),
      });
      setCompanies(res.data.map((c: any) => ({ id: c.id, name: c.name })));
    } catch {
      // keep previous list on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchCompanies(search || undefined);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search, fetchCompanies]);

  // Keep selectedEntry in sync when value changes externally (e.g. reset, or an
  // initial value supplied via URL param). The selected company may not be in the
  // default first-page results, so resolve its name from the list or fetch it by id.
  useEffect(() => {
    if (!value) {
      setSelectedEntry(null);
      return;
    }
    if (selectedEntry?.id === value) return;
    const fromList = companies.find(c => c.id === value);
    if (fromList) {
      setSelectedEntry(fromList);
      return;
    }
    let cancelled = false;
    companyApi.getById(value)
      .then(c => {
        if (!cancelled && c) setSelectedEntry({ id: c.id, name: c.name });
      })
      .catch(() => {
        // leave the current display as-is on error
      });
    return () => {
      cancelled = true;
    };
  }, [value, companies, selectedEntry]);

  // Merge selected entry into the displayed list so the current value always appears
  const displayCompanies = useMemo(() => {
    if (!selectedEntry || companies.some(c => c.id === selectedEntry.id)) {
      return companies;
    }
    return [selectedEntry, ...companies];
  }, [companies, selectedEntry]);

  return (
    <FormControl size="small" sx={{ mb: 0, ...sxProp as any }} error={error}>
      <InputLabel sx={{ mt: '6px' }}>{label}</InputLabel>
      <Select
        sx={{
          '& .MuiSelect-icon': {
            backgroundColor: 'background.paper',
            paddingLeft: '4px',
          },
        }}
        value={value}
        label={label}
        onChange={(e) => {
          const val = e.target.value;
          const c = displayCompanies.find(co => co.id === val);
          const name = c?.name || '';
          if (val && c) setSelectedEntry(c);
          else setSelectedEntry(null);
          onChange(val as number | '', name);
        }}
        onClose={() => setSearch('')}
        renderValue={(selected) => {
          if (!selected) return 'All Licensees';
          const c = selectedEntry?.id === selected
            ? selectedEntry
            : displayCompanies.find(co => co.id === selected);
          return c ? `${c.name} (ID: ${c.id})` : '';
        }}
        MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 300 } } }}
      >
        <ListSubheader sx={{ p: 0, lineHeight: 'normal' }}>
          <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search licensees…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: loading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </ListSubheader>
        {!required && <MenuItem value="">All Licensees</MenuItem>}
        {displayCompanies.map(c => (
          <MenuItem key={c.id} value={c.id}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              <span>{c.name}</span>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ID: {c.id}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        {displayCompanies.length === 0 && !loading && (
          <MenuItem disabled>No licensees found</MenuItem>
        )}
        {loading && displayCompanies.length === 0 && (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} /> Loading…
            </Box>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default LicenseeFilter;
